import { useFetch } from '@/composables/useFetch';
import { useFileDownload } from '@/composables/useFileDownload';
import i18n from '@/i18n';
import { useEnvStore } from '@/stores/envStore';
import type {
  CreateMenuCategoryRequest,
  CreateMenuDishRequest,
  CreateMenuRequest,
  MenuCategory,
  RestaurantMenu,
  UpdateMenuCategoryRequest,
  UpdateMenuDishRequest,
  UpdateMenuRequest,
} from '@/types/menu';
import { useToast } from 'primevue/usetoast';
import { ref, type Ref } from 'vue';
const { t } = i18n.global;

export const useMenus = () => {
  const menus: Ref<RestaurantMenu[]> = ref([]);
  const selectedMenu: Ref<RestaurantMenu | null> = ref(null);
  const menuCategories: Ref<MenuCategory[]> = ref([]);
  const isLoading = ref(false);
  const isImporting = ref(false);

  const { fetchData } = useFetch();
  const { downloadFile, normalizeFileName } = useFileDownload();
  const toast = useToast();

  const extractFilenameFromContentDisposition = (header: string | string[] | undefined): string | null => {
    if (!header) {
      return null;
    }

    const value = Array.isArray(header) ? header[0] : header;
    if (!value) {
      return null;
    }

    const rfc5987Match = value.match(/filename\*\s*=\s*([^;]+)/i);
    if (rfc5987Match?.[1]) {
      let filename = rfc5987Match[1].trim();
      filename = filename.replace(/^UTF-8''/i, '').replace(/^"(.*)"$/, '$1');
      try {
        filename = decodeURIComponent(filename);
      } catch {
        // Keep raw value when URI decoding fails
      }

      return filename || null;
    }

    const classicMatch = value.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);

    return (classicMatch?.[1] ?? classicMatch?.[2] ?? '').trim() || null;
  };

  // Menu operations
  const fetchMenus = async () => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus`, 'GET');

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.fetch.error'),
          life: 5000,
        });
        return false;
      }

      menus.value = data;
      return true;
    } catch (error) {
      console.error('Error fetching menus:', error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchMenu = async (menuId: number) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/${menuId}`, 'GET');

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.fetch.error'),
          life: 5000,
        });
        return null;
      }

      selectedMenu.value = data;
      return data;
    } catch (error) {
      console.error('Error fetching menu:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const createMenu = async (menuData: CreateMenuRequest) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus`, 'POST', menuData);

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.create.error'),
          life: 5000,
        });
        return null;
      }

      menus.value.push(data);
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.create.success'),
        life: 3000,
      });

      return data;
    } catch (error) {
      console.error('Error creating menu:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateMenu = async (menuId: number, menuData: UpdateMenuRequest) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/${menuId}`, 'PUT', menuData);

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.update.error'),
          life: 5000,
        });
        return null;
      }

      const index = menus.value.findIndex((menu) => menu.id === menuId);
      if (index !== -1) {
        menus.value[index] = data;
      }

      if (selectedMenu.value?.id === menuId) {
        selectedMenu.value = data;
      }

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.update.success'),
        life: 3000,
      });

      return data;
    } catch (error) {
      console.error('Error updating menu:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteMenu = async (menuId: number) => {
    isLoading.value = true;
    try {
      const { error } = await fetchData(`${useEnvStore().apiUrl}/menus/${menuId}`, 'DELETE');

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.delete.error'),
          life: 5000,
        });
        return false;
      }

      menus.value = menus.value.filter((menu) => menu.id !== menuId);
      if (selectedMenu.value?.id === menuId) {
        selectedMenu.value = null;
      }

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.delete.success'),
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error('Error deleting menu:', error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Menu category operations
  const fetchMenuCategories = async (menuId: number) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/${menuId}/categories`, 'GET');

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.categories.fetch.error'),
          life: 5000,
        });
        return false;
      }

      menuCategories.value = data;
      return true;
    } catch (error) {
      console.error('Error fetching menu categories:', error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const addCategoryToMenu = async (menuId: number, categoryData: CreateMenuCategoryRequest) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/${menuId}/categories`, 'POST', categoryData);

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.category.add.error'),
          life: 5000,
        });
        return null;
      }

      menuCategories.value.push(data);
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.category.add.success'),
        life: 3000,
      });

      return data;
    } catch (error) {
      console.error('Error adding category to menu:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateMenuCategory = async (categoryId: number, categoryData: UpdateMenuCategoryRequest) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/categories/${categoryId}`, 'PUT', categoryData);

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.category.update.error'),
          life: 5000,
        });
        return null;
      }

      const index = menuCategories.value.findIndex((category) => category.id === categoryId);
      if (index !== -1) {
        menuCategories.value[index] = data;
      }

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.category.update.success'),
        life: 3000,
      });

      return data;
    } catch (error) {
      console.error('Error updating category:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const removeMenuCategory = async (categoryId: number) => {
    isLoading.value = true;
    try {
      const { error } = await fetchData(`${useEnvStore().apiUrl}/menus/categories/${categoryId}`, 'DELETE');

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.category.delete.error'),
          life: 5000,
        });
        return false;
      }

      menuCategories.value = menuCategories.value.filter((category) => category.id !== categoryId);
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.category.delete.success'),
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error('Error removing category:', error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Menu dish operations
  const addDishToCategory = async (categoryId: number, dishData: CreateMenuDishRequest) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/categories/${categoryId}/dishes`, 'POST', dishData);

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.dish.add.error'),
          life: 5000,
        });
        return null;
      }

      // Update the category's dishes array
      const categoryIndex = menuCategories.value.findIndex((category) => category.id === categoryId);
      if (categoryIndex !== -1) {
        if (!menuCategories.value[categoryIndex].dishes) {
          menuCategories.value[categoryIndex].dishes = [];
        }
        menuCategories.value[categoryIndex].dishes!.push(data);
      }

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.dish.add.success'),
        life: 3000,
      });

      return data;
    } catch (error) {
      console.error('Error adding dish to category:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateMenuDish = async (menuDishId: number, dishData: UpdateMenuDishRequest) => {
    isLoading.value = true;
    try {
      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/dishes/${menuDishId}`, 'PUT', dishData);

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.dish.edit.error'),
          life: 5000,
        });
        return null;
      }

      // Update the dish in the categories array
      menuCategories.value.forEach((category) => {
        if (category.dishes) {
          const dishIndex = category.dishes.findIndex((dish) => dish.id === menuDishId);
          if (dishIndex !== -1) {
            category.dishes[dishIndex] = data;
          }
        }
      });

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.dish.edit.success'),
        life: 3000,
      });

      return data;
    } catch (error) {
      console.error('Error updating menu dish:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const removeMenuDish = async (menuDishId: number) => {
    isLoading.value = true;
    try {
      const { error } = await fetchData(`${useEnvStore().apiUrl}/menus/dishes/${menuDishId}`, 'DELETE');

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.dish.delete.error'),
          life: 5000,
        });
        return false;
      }

      // Remove the dish from the categories array
      menuCategories.value.forEach((category) => {
        if (category.dishes) {
          category.dishes = category.dishes.filter((dish) => dish.id !== menuDishId);
        }
      });

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.dish.delete.success'),
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error('Error removing menu dish:', error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // PDF Export
  const exportMenuToPdf = async (menuId: number) => {
    isLoading.value = true;
    try {
      // For PDF export, we need to use axios directly with blob response type
      // since fetchData doesn't support responseType configuration
      const envStore = useEnvStore();
      const axios = (await import('@/utils/axios')).default;

      const response = await axios.get(`${envStore.apiUrl}/menus/${menuId}/export-pdf`, {
        responseType: 'blob', // Important for binary data
      });

      // Get the filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'] ?? response.headers['Content-Disposition'];
      const extractedFilename = extractFilenameFromContentDisposition(contentDisposition);
      const filename = normalizeFileName(extractedFilename ?? 'menu.pdf', 'application/pdf');

      // Create blob and download using useFileDownload
      const blob = new Blob([response.data], { type: 'application/pdf' });

      await downloadFile({
        fileName: filename,
        data: blob,
        mimeType: 'application/pdf',
        showShareDialog: true,
      });

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.export.success'),
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error('Error exporting menu to PDF:', error);

      // Use the same error handling pattern as other functions
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('menu.export.error'),
        life: 5000,
      });
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const importMenu = async (file: File) => {
    isImporting.value = true;
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data, error } = await fetchData(`${useEnvStore().apiUrl}/menus/import`, 'POST', formData, 120000);

      if (error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('menu.import.error'),
          life: 5000,
        });
        return null;
      }

      menus.value.push(data);
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menu.import.success'),
        life: 3000,
      });

      return data;
    } catch (error) {
      console.error('Error importing menu:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('menu.import.error'),
        life: 5000,
      });
      return null;
    } finally {
      isImporting.value = false;
    }
  };

  return {
    // State
    menus,
    selectedMenu,
    menuCategories,
    isLoading,
    isImporting,

    // Menu operations
    fetchMenus,
    fetchMenu,
    createMenu,
    updateMenu,
    deleteMenu,
    importMenu,

    // Category operations
    fetchMenuCategories,
    addCategoryToMenu,
    updateMenuCategory,
    removeMenuCategory,

    // Dish operations
    addDishToCategory,
    updateMenuDish,
    removeMenuDish,

    // PDF Export
    exportMenuToPdf,
  };
};
